import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import {
  TransactionType,
  TransactionFilters,
  TransactionOrderBy,
  TransactionOrder,
  GetTransactionsResponse,
  TransactionDetailState,
  GetTransactionDetailResponse,
  TransactionProjectionStatusEnum,
  mapProjectionStatusToExecutionStep,
  mapFailureCodeToExecutionStep,
  GetSigningNotificationChannelsResponse,
  GetTransactionNetworkDetailsResponse,
  GetTransactionBlockchainDetailsResponse,
  BroadcastTransactionRequestBody,
  BroadcastTransactionResult,
  GetFeeOptionsResponse,
  FeeChoice,
  CancelTransactionRequestBody,
  ExecuteTransactionRequestBody,
  ExecuteTransactionResult,
  CreateWithdrawalRequestBody,
  CreateWithdrawalResponse,
  GetNetworkFeeResponse,
  GetPricingRatesResponse,
  GetTransactionStep01DetailsResponse,
  GetWithdrawalLimitSummaryResponse,
} from '../models/transaction.model';
import type { VerificationRequiredResponse } from '@shared/models/verification.model';
import type { GetApprovalProgressResponse } from '@/app/shared/models/approval.model';
import {
  inclusiveDayEndIso,
  inclusiveDayStartIso,
} from '@shared/utils/filter-date-range';

@Injectable({
  providedIn: 'root',
})
export class TransactionService {
  private readonly baseUrl = `${environment.transactionManagementApiUrl}/api/v1`;
  private readonly assetManagementApiUrl = `${environment.assetManagementApiUrl}/api/v1`;

  /** Mock: after successful broadcast, network-details returns BROADCASTED + hash */
  private readonly mockBroadcastConfirmedIds = new Set<string>();

  constructor(private http: HttpClient) {}

  getTransactions(
    type: TransactionType,
    page: number,
    limit: number,
    filters?: TransactionFilters,
    searchTerm?: string,
    orderBy?: TransactionOrderBy,
    order?: TransactionOrder
  ): Observable<GetTransactionsResponse> {
    const orderByVal = orderBy ?? 'createdAt';
    const orderVal = order ?? 'DESC';

    let params = new HttpParams()
      .set('page', String(page))
      .set('limit', String(limit))
      .set('orderBy', orderByVal)
      .set('order', orderVal);

    if (type) {
      params = params.set('type', type);
    }
    const startDateIso = inclusiveDayStartIso(filters?.fromDate);
    if (startDateIso) params = params.set('startDate', startDateIso);
    const endDateIso = inclusiveDayEndIso(filters?.toDate);
    if (endDateIso) params = params.set('endDate', endDateIso);
    if (filters?.relatedVaultId)
      params = params.set('vaultId', filters.relatedVaultId);
    if (filters?.relatedWalletId)
      params = params.set('walletId', filters.relatedWalletId);
    if (filters?.requesterIds?.length) {
      filters.requesterIds.forEach(
        id => (params = params.append('createdBy', id))
      );
    }
    if (filters?.status) params = params.set('status', filters.status);
    if (searchTerm) params = params.set('search', searchTerm);

    return this.http.get<GetTransactionsResponse>(
      `${this.baseUrl}/transaction`,
      { params }
    );
  }

  getNetworkFee(walletId: string): Observable<GetNetworkFeeResponse> {
    return this.http.get<GetNetworkFeeResponse>(
      `${this.baseUrl}/wallet/${walletId}/fee`
    );
  }

  /**
   * GET /api/v1/wallet/:walletId/withdrawal/limit-summary
   */
  getWithdrawalLimitSummary(
    walletId: string
  ): Observable<GetWithdrawalLimitSummaryResponse> {
    return this.http.get<GetWithdrawalLimitSummaryResponse>(
      `${this.assetManagementApiUrl}/wallet/${walletId}/withdrawal/limit-summary`
    );
  }

  /**
   * GET /api/v1/pricing/rates?base=BTC,ETH&quote=USDT
   */
  getPricingRates(
    baseSymbols: string[],
    quoteSymbol: string = 'USDT'
  ): Observable<GetPricingRatesResponse> {
    const base = (baseSymbols ?? [])
      .map(s => String(s ?? '').trim())
      .filter(Boolean)
      .join(',');
    const params = new HttpParams().set('base', base).set('quote', quoteSymbol);
    return this.http.get<GetPricingRatesResponse>(
      `${this.assetManagementApiUrl}/pricing/rates`,
      { params }
    );
  }

  /**
   * GET /v1/transaction/:transactionId (mocked for now).
   * Returns basic transaction info including projection `state` and optional `failure`.
   * The detail screen derives {@link TransactionDetailState.activeStep} via
   * {@link mapToTransactionDetailState}; compare that step across responses to decide when to
   * move the UI to a new execution step.
   */
  getTransactionById(
    transactionId: string
  ): Observable<GetTransactionDetailResponse> {
    const url = `${this.baseUrl}/transaction/${transactionId}`;
    return this.http.get<GetTransactionDetailResponse>(url);
  }

  /**
   * GET /api/v1/approval-requests/external/:externalRequestId/progress
   * Used by transaction step-02 (approvals) based on `approvalRequestId` returned from transaction detail.
   */
  getApprovalRequestProgressByExternalId(externalRequestId: string) {
    const baseUrl = `${environment.policyManagementApiUrl}/api/v1/approval-requests`;
    const id = encodeURIComponent(String(externalRequestId ?? '').trim());
    return this.http.get<GetApprovalProgressResponse>(
      `${baseUrl}/external/${id}/progress`
    );
  }

  /**
   * GET /api/v1/transaction/:transactionId/details — withdrawal overview for step 1.
   */
  getTransactionStep01Details(
    transactionId: string
  ): Observable<GetTransactionStep01DetailsResponse> {
    const url = `${this.baseUrl}/transaction/${transactionId}/details`;
    return this.http.get<GetTransactionStep01DetailsResponse>(url);
  }

  /**
   * Maps /v1/transaction/:id response to detail stepper state.
   */
  mapToTransactionDetailState(
    response: GetTransactionDetailResponse
  ): TransactionDetailState {
    const { state, failure, pendingReason } = response.data.status;
    const projectionStatus = state;
    const activeStep =
      projectionStatus === TransactionProjectionStatusEnum.FAILED
        ? failure
          ? mapFailureCodeToExecutionStep(failure.code)
          : 1
        : mapProjectionStatusToExecutionStep(projectionStatus);
    const hasFailureOnActiveStep = Boolean(failure);
    const lastApprovedStep = hasFailureOnActiveStep
      ? (Math.max(
          1,
          activeStep - 1
        ) as TransactionDetailState['lastApprovedStep'])
      : activeStep;

    return {
      vaultName: response.data.wallet.vaultName,
      activeStep,
      lastApprovedStep,
      hasFailureOnActiveStep,
      projectionStatus,
      failure,
      pendingReason: pendingReason ?? null,
    };
  }

  /**
   * GET /v1/transaction/signing-notification-channels (mocked for now).
   * Returns signature step status and notification channels.
   */
  getSigningNotificationChannels(
    _transactionId?: string
  ): Observable<GetSigningNotificationChannelsResponse> {
    const url = `${this.baseUrl}/transaction/signing-notification-channels`;

    return this.http.get<GetSigningNotificationChannelsResponse>(url);
  }

  /**
   * GET /v1/transaction/:transactionId/network-details
   * Network step payload (mocked unless USE_REAL_API).
   */
  getTransactionNetworkDetails(
    transactionId: string
  ): Observable<GetTransactionNetworkDetailsResponse> {
    const url = `${this.baseUrl}/transaction/${transactionId}/network-details`;

    return this.http.get<GetTransactionNetworkDetailsResponse>(url);
  }

  /**
   * GET /v1/transaction/:transactionId/blockchian-details
   * (Path spelling matches backend contract.)
   */
  getTransactionBlockchainDetails(
    transactionId: string
  ): Observable<GetTransactionBlockchainDetailsResponse> {
    const url = `${this.baseUrl}/transaction/${transactionId}/blockchain-details`;
    return this.http.get<GetTransactionBlockchainDetailsResponse>(url);
  }

  createWithdrawal(
    walletId: string,
    body: CreateWithdrawalRequestBody
  ): Observable<HttpResponse<CreateWithdrawalResponse>> {
    const url = `${this.assetManagementApiUrl}/wallet/${walletId}/withdrawal`;
    return this.http.post<CreateWithdrawalResponse>(url, body, {
      observe: 'response',
    });
  }

  /**
   * POST /v1/transaction/:transactionId/broadcast (initial call — no body).
   * Returns 202 with verification metadata if a fresh actionId is required, or 200 with the
   * transaction detail if the guard finds a valid cached actionId.
   */
  broadcastTransaction(
    transactionId: string
  ): Observable<BroadcastTransactionResult> {
    const url = `${this.baseUrl}/transaction/${transactionId}/broadcast`;

    return this.http
      .post<
        VerificationRequiredResponse | unknown
      >(url, {}, { observe: 'response' })
      .pipe(
        switchMap(res => {
          if (res.status === 202) {
            const data = res.body as VerificationRequiredResponse;
            return of<BroadcastTransactionResult>({
              type: 'verification_required',
              data,
            });
          }
          return this.getTransactionById(transactionId).pipe(
            switchMap(response =>
              of<BroadcastTransactionResult>({ type: 'success', response })
            )
          );
        })
      );
  }

  /**
   * POST /v1/transaction/:transactionId/broadcast (confirmation call with `actionId`).
   * Issued after the user-submitted code is verified by verification-management.
   */
  confirmBroadcast(
    transactionId: string,
    actionId: string
  ): Observable<GetTransactionDetailResponse> {
    const url = `${this.baseUrl}/transaction/${transactionId}/broadcast`;
    const body: BroadcastTransactionRequestBody = { actionId };
    return this.http
      .post(url, body)
      .pipe(switchMap(() => this.getTransactionById(transactionId)));
  }

  /**
   * GET /v1/transaction/:transactionId/fee-options — tiered fee suggestions for a
   * withdrawal sitting in AWAITING_EXECUTION. Re-quoted by the backend at Execute.
   */
  getFeeOptions(transactionId: string): Observable<GetFeeOptionsResponse> {
    const url = `${this.baseUrl}/transaction/${transactionId}/fee-options`;
    return this.http.get<GetFeeOptionsResponse>(url);
  }

  /**
   * POST /v1/transaction/:transactionId/execute (initial call — `feeChoice` only).
   * Returns 202 with verification metadata when a fresh actionId is required, or 200
   * with the transaction detail if the guard finds a valid cached actionId.
   */
  executeTransaction(
    transactionId: string,
    feeChoice: FeeChoice
  ): Observable<ExecuteTransactionResult> {
    const url = `${this.baseUrl}/transaction/${transactionId}/execute`;
    const body: ExecuteTransactionRequestBody = { feeChoice };

    return this.http
      .post<
        VerificationRequiredResponse | unknown
      >(url, body, { observe: 'response' })
      .pipe(
        switchMap(res => {
          if (res.status === 202) {
            const data = res.body as VerificationRequiredResponse;
            return of<ExecuteTransactionResult>({
              type: 'verification_required',
              data,
            });
          }
          return this.getTransactionById(transactionId).pipe(
            switchMap(response =>
              of<ExecuteTransactionResult>({ type: 'success', response })
            )
          );
        })
      );
  }

  /**
   * POST /v1/transaction/:transactionId/execute (confirmation call with `actionId`).
   * Issued after the user-submitted code is verified. `feeChoice` must match the one
   * sent on the initial call — the verification challenge is fingerprinted on it.
   */
  confirmExecute(
    transactionId: string,
    actionId: string,
    feeChoice: FeeChoice
  ): Observable<GetTransactionDetailResponse> {
    const url = `${this.baseUrl}/transaction/${transactionId}/execute`;
    const body: ExecuteTransactionRequestBody = { actionId, feeChoice };
    return this.http
      .post(url, body)
      .pipe(switchMap(() => this.getTransactionById(transactionId)));
  }

  /**
   * POST /v1/transaction/:transactionId/cancel (initial call). OWNER only; a
   * withdrawal still waiting for Execute is cancelled and its held amount
   * released. Returns 202 with verification metadata, or 200 with the detail.
   */
  cancelTransaction(
    transactionId: string,
    reason: string | null
  ): Observable<ExecuteTransactionResult> {
    const url = `${this.baseUrl}/transaction/${transactionId}/cancel`;
    const body: CancelTransactionRequestBody = reason ? { reason } : {};

    return this.http
      .post<
        VerificationRequiredResponse | unknown
      >(url, body, { observe: 'response' })
      .pipe(
        switchMap(res => {
          if (res.status === 202) {
            return of<ExecuteTransactionResult>({
              type: 'verification_required',
              data: res.body as VerificationRequiredResponse,
            });
          }
          return this.getTransactionById(transactionId).pipe(
            switchMap(response =>
              of<ExecuteTransactionResult>({ type: 'success', response })
            )
          );
        })
      );
  }

  /**
   * POST /v1/transaction/:transactionId/cancel (confirmation call with `actionId`),
   * carrying the same `reason` as the initial call.
   */
  confirmCancel(
    transactionId: string,
    actionId: string,
    reason: string | null
  ): Observable<GetTransactionDetailResponse> {
    const url = `${this.baseUrl}/transaction/${transactionId}/cancel`;
    const body: CancelTransactionRequestBody = reason
      ? { actionId, reason }
      : { actionId };
    return this.http
      .post(url, body)
      .pipe(switchMap(() => this.getTransactionById(transactionId)));
  }
}
